import React, { useState } from 'react';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../AuthContext';
import { useNavigate } from 'react-router-dom';
import { createCheckoutSession, createGuestCheckoutSession, isTestMode, isStripeConfigured, TestModeBanner } from '../../services/stripe';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../translations';
import './CheckoutPage.css';

function CheckoutPage() {
  const { items, getCartTotal } = useCart();
  const { currentUser } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [orderProcessing, setOrderProcessing] = useState(false);
  const [specialRequirements, setSpecialRequirements] = useState('');

  const hasEventItems = items.some(item => item.isEvent || item.type === 'event' || item.category === 'Event');

  // Member pricing disabled for now — uncomment when membership program is active
  // const getDiscountedTotal = () => {
  //   return items.reduce((total, item) => {
  //     const itemPrice = isMember() ? getMemberPrice(item.price) : item.price;
  //     return total + (itemPrice * item.quantity);
  //   }, 0);
  // };
  // const getTotalSavings = () => {
  //   if (!isMember()) return 0;
  //   return getCartTotal() - getDiscountedTotal();
  // };

  const handleProceedToPayment = async () => {
    if (!isStripeConfigured()) {
      alert('Payment system is not available at the moment. Please try again later.');
      return;
    }

    setOrderProcessing(true);

    try {
      // Prepare items for Stripe
      const stripeItems = items.map(item => ({
        name: item.name,
        description: item.category,
        amount: Math.round(item.price * 100),
        currency: 'sek',
        quantity: item.quantity,
        metadata: {
          itemId: item.productId || item.id,
          variant: item.variant || null,
          isEvent: String(item.isEvent || item.type === 'event' || item.category === 'Event' || false),
          eventId: item.eventId || item.id || null,
          eventDate: item.eventDate || null,
          venue: item.venue || null,
          ticketType: item.ticketType || null,
          category: item.category || 'general'
        }
      }));

      const successUrl = `${window.location.origin}/order-success?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${window.location.origin}/cart`;
      const notes = hasEventItems ? specialRequirements.trim() : '';

      // Use authenticated checkout if logged in (preserves member pricing link),
      // otherwise use guest checkout
      if (currentUser) {
        await createCheckoutSession(stripeItems, currentUser.email, successUrl, cancelUrl, notes);
      } else {
        await createGuestCheckoutSession(stripeItems, successUrl, cancelUrl, notes);
      }

      // Stripe handles the redirect
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setOrderProcessing(false);
      alert('There was an error processing your payment. Please try again.');
    }
  };

  if (items.length === 0) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <h1>YOUR BASKET IS EMPTY</h1>
          <p>Add some items to your basket before checking out.</p>
          <button onClick={() => navigate('/shop')} className="shop-btn">
            GO TO SHOP
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <TestModeBanner />
      <div className="checkout-container">
        <h1>CHECKOUT</h1>

        {isTestMode() && (
          <div style={{
            background: '#d1f2eb',
            padding: '15px',
            marginBottom: '20px',
            borderRadius: '5px',
            border: '1px solid #5cb85c'
          }}>
            <strong>Test Mode Active</strong>
            <p style={{ margin: '5px 0 0 0', fontSize: '14px' }}>
              Use test card: 4242 4242 4242 4242 (any future date, any CVC)
            </p>
          </div>
        )}

        <div className="checkout-content">
          <div className="order-summary">
            <h2>ORDER SUMMARY</h2>

            {items.map(item => (
              <div key={item.id} className="checkout-item">
                <div className="item-info">
                  <h3>{item.name}</h3>
                  <p>Quantity: {item.quantity}</p>
                  {item.formattedDate && (
                    <p style={{ fontSize: '0.85rem', color: '#666' }}>{item.formattedDate}</p>
                  )}
                </div>
                <div className="item-pricing">
                  <p><strong>{(item.price * item.quantity).toFixed(2)} SEK</strong></p>
                </div>
              </div>
            ))}

            <div className="order-totals">
              <div className="total-line final">
                <span>Total:</span>
                <span>{getCartTotal().toFixed(2)} SEK</span>
              </div>
            </div>
          </div>

          <div className="checkout-form">
            <p style={{ fontSize: '0.95rem', color: '#555', lineHeight: '1.5', marginBottom: '1.5rem' }}>
              You'll enter your contact details and payment information on the next page.
              {items.some(item => item.postage) && ' Shipping address will also be collected.'}
              {items.some(item => item.variant === 'download') && ' Download links will be emailed to you after payment.'}
            </p>

            {hasEventItems && (
              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="specialRequirements" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  {language === 'sv'
                    ? 'Särskilda önskemål, t.ex. kost (valfritt)'
                    : 'Special requirements, e.g. dietary (optional)'}
                </label>
                <textarea
                  id="specialRequirements"
                  name="specialRequirements"
                  value={specialRequirements}
                  onChange={(e) => setSpecialRequirements(e.target.value)}
                  rows="3"
                  maxLength={450}
                  placeholder={language === 'sv' ? 't.ex. vegetarisk, glutenfri, rullstol...' : 'e.g. vegetarian, gluten-free, wheelchair...'}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontFamily: 'inherit',
                    fontSize: '0.95rem',
                    resize: 'vertical'
                  }}
                />
              </div>
            )}

            <div className="checkout-actions">
              <button
                onClick={() => navigate('/cart')}
                className="back-btn"
              >
                BACK TO BASKET
              </button>
              <button
                onClick={handleProceedToPayment}
                disabled={orderProcessing}
                className="place-order-btn"
                style={{
                  background: isTestMode() ? '#28a745' : '#000',
                  color: '#fff'
                }}
              >
                {orderProcessing ? 'REDIRECTING TO PAYMENT...' : 'PROCEED TO PAYMENT'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckoutPage;
